import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  CircleDashed,
  Cloud,
  RotateCcw,
  Undo2,
} from 'lucide-react'

import { Button, ReceiptsFilters, ReceiptsTable, Workbench, type ReceiptsFiltersProps } from '@krp/ui-kit'

import { useState } from 'react'

import '../../../shared/uc1b/journals.css'
import { QueryState } from '../../../shared/uc1b/QueryState'
import { ReceiptsPlacePicker } from './ReceiptsPlacePicker'
import { RequestProgress } from './RequestProgress'
import type { ReceiptKindFilter, ReceiptsActions, ReceiptsVm } from '../model/types'
import './receipts.css'

/**
 * То, чего нет у «Чеки» и что приносит только «Статистика по продажам» (RP-FB74): итоги, график, прогноз и их сообщения.
 * Тип — срез свойств общих фильтров; этот файл сводок не знает и не импортирует.
 */
export type ReceiptsBrowserExtras = Pick<
  ReceiptsFiltersProps,
  'stats' | 'statsBusy' | 'notices' | 'trend' | 'periodTrend' | 'trendBusy' | 'todayBusy' | 'trendMessage'
>

/**
 * Общая основа вкладок «Чеки» и «Статистика по продажам» (RP-FB74): сообщения источника, сверка журнала, дерево «Где
 * смотрим» (кабинет → магазин → касса → смена), фильтры (период, день, вид, кассир, поиск) и список чеков со страницами.
 * Одна на обе вкладки: ни копии, ни урезанной версии. Сводки сюда приходят только через `extras`.
 */
export function ReceiptsBrowser({
  vm,
  actions,
  extras = {},
  multiDayText,
  receipts = true,
}: {
  vm: ReceiptsVm
  actions: ReceiptsActions
  extras?: ReceiptsBrowserExtras
  /** Что сказать, когда выбран период из нескольких дней и список чеков не выгружается. */
  multiDayText: string
  /** Список чеков и поиск по номеру; у «Статистики по продажам» их нет — там только итоги и графики, чеки не читаются. */
  receipts?: boolean
}) {
  const ready = vm.list.status === 'ready'
  // Чек возврата, к которому перешли по ссылке: его строка подсвечена и прокручена в видимую часть.
  const [focus, setFocus] = useState<string | null>(null)
  const openRefund = (refund: { id: string; day: string }) => {
    if (vm.stats.kind !== '') actions.kindChanged('')
    actions.periodChanged({ from: refund.day, to: refund.day })
    setFocus(refund.id)
  }
  const nav = vm.nav
  return (
    <>
      {vm.sourceMessage !== null && (
        <p className="aqsi-journal__note" data-tone="warning" role="status">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>Источник недоступен: {vm.sourceMessage}</span>
        </p>
      )}
      {vm.warning !== null && (
        <p className="aqsi-journal__note" data-tone="warning" role="status">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{vm.warning}</span>
        </p>
      )}
      {vm.originText !== null && (
        <p className="aqsi-journal__note" role="status">
          <Cloud size={16} aria-hidden="true" />
          <span>{vm.originText}</span>
        </p>
      )}
      {receipts && vm.period.listShown && <RequestProgress query={vm.list} what="Чеки" />}

      <section className="aqsi-journal__sync" aria-label="Сверка журнала">
        <p>
          <strong>{vm.journal.statusText}</strong>
          {vm.journal.known && <> · последний успех: {vm.journal.syncedAtText}</>}
          {vm.journal.known && <> · {vm.journal.detail}</>}
        </p>
        {vm.journal.error !== null && <p role="status">Причина: {vm.journal.error}</p>}
        {vm.journal.query.status === 'forbidden' || vm.journal.query.status === 'unavailable' || vm.journal.query.status === 'error' ? (
          <p role="status">Статус сверки недоступен: {vm.journal.query.error?.message}</p>
        ) : null}
        {vm.journal.failure !== null && (
          <p role="alert">
            Сверку поставить не удалось: {vm.journal.failure.message} (код {vm.journal.failure.code}
            {vm.journal.failure.correlationId !== null ? `, запрос ${vm.journal.failure.correlationId}` : ''})
          </p>
        )}
        <Button
          variant="secondary"
          data-size="sm"
          icon={<RotateCcw size={16} aria-hidden="true" />}
          busy={vm.journal.active}
          onClick={actions.syncJournal}
        >
          {vm.journal.active ? 'Сверка идёт…' : 'Сверить журнал'}
        </Button>
      </section>

      <Workbench
        sideTitle="Где смотрим"
        sideSummary={nav.summaryText}
        side={
          nav.query.status === 'ready' ? (
            <ReceiptsPlacePicker nav={nav} onSelect={actions.nodeChosen} onToggle={actions.nodeToggled} onRetry={actions.reload} />
          ) : (
            <QueryState query={nav.query} emptyText="Кассы кабинета не найдены" onRetry={actions.reload} />
          )
        }
      >
        <ReceiptsFilters
          today={vm.period.today}
          period={vm.period.range}
          periodNote={vm.period.shiftNote}
          onPeriod={actions.periodChanged}
          searchShown={receipts && vm.period.listShown}
          {...extras}
          kinds={vm.stats.kinds}
          kind={vm.stats.kind}
          onKind={(kind) => actions.kindChanged(kind as ReceiptKindFilter)}
          cashiers={vm.stats.cashiers}
          cashier={vm.stats.cashier}
          onCashier={actions.cashierChanged}
          cashierHint="За выбранный период и место кассиров нет или он один: выбирать некого."
          search={vm.draft.search}
          searchLabel="Поиск по номеру, сумме, фискальному признаку"
          onSearch={(search) => actions.draftChanged({ search })}
          onSearchSubmit={actions.apply}
          onSearchReset={actions.reset}
          searchPending={vm.dirty}
          days={vm.stats.days}
          onDay={(day) => actions.periodChanged({ from: day, to: day })}
        />

        {receipts && !vm.period.listShown && (
          <p className="aqsi-journal__note" role="status">
            {multiDayText}
          </p>
        )}
        {receipts && vm.period.listShown && (
          <>
        <QueryState query={vm.list} emptyText="В этом месте за выбранный период чеков нет. Число справа от названия в дереве — сколько чеков у места за период: выберите другое место или период." onRetry={actions.reload} />
        {!ready && vm.page.hasPrevious && (
          <div className="aqsi-journal__actions">
            <Button icon={<ArrowLeft size={16} aria-hidden="true" />} onClick={actions.previousPage}>
              Назад
            </Button>
          </div>
        )}
        {vm.cursorProblem && (
          <div className="aqsi-journal__actions">
            <Button variant="primary" onClick={actions.restart}>
              Начать выдачу заново
            </Button>
          </div>
        )}

        {ready && (
          <>
            {vm.rows.length === 0 ? (
              <p className="aqsi-journal__note">На этой странице подходящих чеков нет — продолжите выдачу.</p>
            ) : (
              <ReceiptsTable label="Чеки" rows={vm.rows} focusId={focus} onOpen={(row) => actions.openReceipt(row.id)} columns={[
                { id: 'shift', label: 'Смена', cell: (row) => row.shiftText, title: (row) => row.shiftText },
                { id: 'state', label: 'Состояние', title: (row) => [row.confirmText, row.returned ? row.returnedText : null, row.note].filter((part) => part !== null).join(' · '),
                  cell: (row) => <span className="aqsi-receipts__state">
                    {row.confirmed ? <CheckCircle2 size={14} aria-hidden="true" /> : <CircleDashed size={14} aria-hidden="true" />} {row.confirmText}
                    {row.returned && <><Undo2 size={14} aria-hidden="true" /> {row.returnedText}</>}
                    {row.note !== null && <><AlertTriangle size={14} aria-hidden="true" /> {row.note}</>}
                  </span> },
                { id: 'refunds', label: 'Возвраты', cell: (row) => row.refunds.map((refund) =>
                  <Button key={refund.id} variant="ghost" data-size="sm" onClick={() => openRefund(refund)}>{refund.label}</Button>) },
              ]} />
            )}
            <nav className="aqsi-journal__pager" aria-label="Страницы журнала">
              <Button
                icon={<ArrowLeft size={16} aria-hidden="true" />}
                disabled={!vm.page.hasPrevious}
                onClick={actions.previousPage}
              >
                Назад
              </Button>
              <span aria-live="polite">
                Страница {vm.page.number} · всего по отбору без кассира и поиска: {vm.page.totalCount}
              </span>
              {vm.page.nextCursor !== null ? (
                <Button
                  icon={<ArrowRight size={16} aria-hidden="true" />}
                  onClick={() => vm.page.nextCursor !== null && actions.nextPage(vm.page.nextCursor)}
                >
                  Дальше
                </Button>
              ) : (
                <span>{vm.page.complete ? 'Конец журнала' : 'Дальше страниц нет'}</span>
              )}
            </nav>
          </>
        )}
          </>
        )}
      </Workbench>
    </>
  )
}
